import posthog, { type PostHogConfig } from 'posthog-js';
import { env } from '@/lib/env';

let initialized = false;

export function getPostHogClient(): typeof posthog | null {
  if (typeof window === 'undefined') return null;

  if (!initialized) {
    const key = env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return null;

    // `asset_host` is a documented runtime option in posthog-js, but it's not
    // in the 1.367 .d.ts yet — cast to satisfy tsc. Drop the cast once the
    // dep is bumped.
    const config: Partial<PostHogConfig> & { asset_host?: string } = {
      api_host: '/ingest',
      // Static JS chunks (web-vitals, exception-autocapture, surveys, etc.) are
      // fetched directly from PostHog's CDN rather than through the /ingest proxy.
      // The proxy forwards all cookies, which makes the request header too large
      // for PostHog's CDN and causes HTTP 431. Static assets don't need auth so
      // bypassing the proxy here is safe and fixes the 431 errors.
      asset_host: 'https://us-assets.i.posthog.com',
      ui_host: 'https://us.posthog.com',   // PostHog app — for toolbar, NOT the API host
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
    };
    posthog.init(key, config as Partial<PostHogConfig>);
    initialized = true;
  }

  return posthog;
}

/**
 * Convenience wrapper — capture an event only if PostHog is available.
 * Avoids repetitive `getPostHogClient()?.capture(...)` at every call site.
 */
export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  getPostHogClient()?.capture(event, properties);
}

/**
 * Captures an exception with full stack trace via PostHog's $exception event.
 * Use this everywhere an error is caught — replaces or augments console.error.
 */
export function captureException(
  error: unknown,
  properties?: Record<string, unknown>,
): void {
  getPostHogClient()?.captureException(error, properties);
}
